import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Plus, Trash2, Loader2, ExternalLink, Send, Copy, ClipboardCheck,
  Sparkles, X, RotateCcw, Repeat,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { logger } from '../../utils/logger';
import ConsultationSummaryPreview from './ConsultationSummaryPreview';
import { ConsultationEmailReviewDialog } from '../../components/admin/ConsultationEmailReviewDialog';
import { ConsultationClientLink } from '../../components/admin/ConsultationClientLink';
import { ApprovedConsultationPdf } from '../../components/admin/ApprovedConsultationPdf';
import { ConsultationPdfViewer } from '../../components/admin/ConsultationPdfViewer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  title: '',
  client_name: '',
  client_email: '',
  client_phone: '',
  client_address: '',
  client_id: null,
  client_sync: null,
  linked_quote_id: null,
  linked_quote_number: null,
  origin: '',
  destination: '',
  transit_via: '',
  transit_stops: [],
  pet_names: '',
  consultation_date: '',
  attendees: '',
  intro: '',
  discussion_points: [''],
  next_steps: [{ action: '', owner: '', due_date: '' }],
  timeline_items: [{ when: '', milestone: '' }],
  notes: '',
  closing_note: '',
};

export const ConsultationSummaryEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;
  const password = sessionStorage.getItem('admin_password');
  const auth = useMemo(() => ({ Authorization: `Basic ${btoa(':' + password)}` }), [password]);

  const [form, setForm] = useState(EMPTY);
  const [savedId, setSavedId] = useState(id || null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [preparingEmail, setPreparingEmail] = useState(false);
  const preparingEmailRef = useRef(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [quotes, setQuotes] = useState([]);
  const [snippets, setSnippets] = useState([]);
  const [templates, setTemplates] = useState([]); // list of {id, name, next_steps, timeline_items}
  const [tplPickerOpen, setTplPickerOpen] = useState(false);
  const [appliedTemplate, setAppliedTemplate] = useState(null); // {id, name, source: 'auto'|'manual', prior: {next_steps, timeline_items}}
  const [snippetPicker, setSnippetPicker] = useState(null); // {kind, onPick}
  const [emailDraft, setEmailDraft] = useState(null);
  const [approvedPdfState, setApprovedPdfState] = useState(null);

  // ---------------------------------------------------------------
  // Load
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!password) { navigate('/admin/login'); return; }
    (async () => {
      try {
        if (!isNew) {
          const { data } = await axios.get(`${API}/admin/consultation-summaries/${id}`, { headers: auth });
          setForm({
            ...EMPTY, ...data,
            discussion_points: data.discussion_points?.length ? data.discussion_points : [''],
            next_steps:        data.next_steps?.length        ? data.next_steps        : [{ action: '', owner: '', due_date: '' }],
            timeline_items:    data.timeline_items?.length    ? data.timeline_items    : [{ when: '', milestone: '' }],
            transit_stops:     data.transit_stops             || [],
          });
        }
        const [qRes, sRes, tRes] = await Promise.all([
          axios.get(`${API}/admin/quotes`, { headers: auth }).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/consultation-snippets`, { headers: auth }).catch(() => ({ data: { items: [] } })),
          axios.get(`${API}/admin/settings/consultation-templates`, { headers: auth }).catch(() => ({ data: null })),
        ]);
        const qList = Array.isArray(qRes.data) ? qRes.data : (qRes.data.items || qRes.data.quotes || []);
        setQuotes(qList.filter((q) => !q.deleted));
        setSnippets(sRes.data.items || []);
        setTemplates(tRes.data?.templates || []);
      } catch (e) {
        logger.error('load summary failed', e);
        toast.error('Failed to load');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------
  // Field helpers
  // ---------------------------------------------------------------
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const setListValue = (listKey, i, field, v) => setForm((p) => {
    const arr = [...p[listKey]];
    if (typeof arr[i] === 'string') arr[i] = v;
    else arr[i] = { ...arr[i], [field]: v };
    return { ...p, [listKey]: arr };
  });

  const addListRow = (listKey, shape) => setForm((p) => ({ ...p, [listKey]: [...p[listKey], typeof shape === 'string' ? '' : { ...shape }] }));
  const removeListRow = (listKey, i) => setForm((p) => ({ ...p, [listKey]: p[listKey].filter((_, idx) => idx !== i) }));

  // Destination → template alias table so "Cayman → UK" matches "United Kingdom",
  // "US" matches "United States", etc. Values are lowercase tokens.
  const DESTINATION_ALIASES = {
    'united kingdom': ['uk', 'britain', 'great britain', 'england', 'scotland', 'wales', 'northern ireland'],
    'united states': ['us', 'usa', 'america'],
    'european union': ['eu', 'europe'],
  };

  const findTemplateForDestination = (dest, tpls) => {
    if (!dest) return null;
    const d = dest.toLowerCase().trim();
    // Build a bag of tokens to search for in template names.
    const tokens = new Set([d]);
    for (const [long, aliases] of Object.entries(DESTINATION_ALIASES)) {
      if (d === long || d.includes(long) || aliases.some((a) => d === a || d.includes(` ${a} `) || d.endsWith(` ${a}`) || d.startsWith(`${a} `) || d === a)) {
        tokens.add(long);
        aliases.forEach((a) => tokens.add(a));
      }
    }
    // Rank templates by token match — first match wins.
    for (const t of tpls) {
      const name = (t.name || '').toLowerCase();
      for (const tok of tokens) {
        if (!tok) continue;
        // whole-word or substring match, but token must be ≥ 2 chars to avoid false hits
        if (tok.length >= 2 && (name === tok || name.includes(` ${tok}`) || name.endsWith(tok) || name.startsWith(`${tok} `))) {
          return t;
        }
      }
    }
    return null;
  };

  const onSelectQuote = (qid) => {
    if (!qid) {
      setForm((p) => ({ ...p, linked_quote_id: null, linked_quote_number: null }));
      return;
    }
    const q = quotes.find((x) => x.id === qid);
    setForm((p) => {
      const next = {
        ...p,
        linked_quote_id: qid,
        linked_quote_number: q?.quote_number || null,
        // Only fill blanks — never blow away manual edits
        client_name:  p.client_name  || q?.client_name  || '',
        client_email: p.client_email || q?.client_email || '',
        client_phone: p.client_phone || q?.client_phone || '',
        destination:  p.destination  || q?.destination  || '',
        origin:       p.origin       || q?.origin       || '',
      };
      // Auto-apply a matching template — only when the summary is still empty,
      // so we never trample manual edits.
      const nsEmpty = isEffectivelyEmpty(p.next_steps, ['action', 'owner', 'due_date']);
      const tlEmpty = isEffectivelyEmpty(p.timeline_items, ['when', 'milestone', 'when_date']);
      if (nsEmpty && tlEmpty) {
        const match = findTemplateForDestination(next.destination, templates);
        if (match) {
          next.next_steps = (match.next_steps || []).map((s) => ({ ...s }));
          next.timeline_items = (match.timeline_items || []).map((t) => ({ ...t }));
          // Record the applied template banner + prior state (empty here since
          // we only auto-apply when both lists are empty).
          setAppliedTemplate({
            id: match.id || null,
            name: match.name,
            source: 'auto',
            prior: {
              next_steps: JSON.parse(JSON.stringify(p.next_steps || [])),
              timeline_items: JSON.parse(JSON.stringify(p.timeline_items || [])),
            },
          });
          // Toast queued outside the setter to keep this pure.
          setTimeout(() => toast.success(`Auto-prefilled from "${match.name}" (matched destination)`), 0);
        }
      }
      return next;
    });
  };

  // Fallback template used only if the admin-editable settings couldn't be fetched.
  const FALLBACK_NEXT_STEPS = [
    { action: 'Book pre-travel vet visit for microchip verification, health check and paperwork audit', owner: 'Client',   due_date: 'TBD' },
    { action: 'Rabies vaccination (if booster or new certificate is needed)',                            owner: 'Client',   due_date: 'TBD' },
    { action: 'Rabies titre blood draw (minimum 30 days after vaccination)',                             owner: 'Client',   due_date: 'TBD' },
    { action: 'Confirm crate sizing and IATA certification',                                             owner: 'Pawsome',  due_date: 'TBD' },
    { action: 'Book flight and confirm cargo booking with the airline',                                  owner: 'Pawsome',  due_date: 'TBD' },
    { action: 'Pre-travel vet inspection and final health certificate',                                  owner: 'Client',   due_date: 'TBD' },
    { action: 'USDA / DEFRA endorsement of the health certificate',                                      owner: 'Pawsome',  due_date: 'TBD' },
    { action: 'File Import Notification and confirm ARC / border slot',                                  owner: 'Pawsome',  due_date: 'TBD' },
  ];

  const FALLBACK_TIMELINE = [
    { when: 'T-8 weeks', milestone: 'Vet visit & paperwork audit',            when_date: '' },
    { when: 'T-7 weeks', milestone: 'Rabies vaccination (if needed)',         when_date: '' },
    { when: 'T-6 weeks', milestone: 'Rabies titre blood draw',                when_date: '' },
    { when: 'T-3 weeks', milestone: 'Confirm crate & book flight',            when_date: '' },
    { when: 'T-1 week',  milestone: 'Pre-travel vet inspection',              when_date: '' },
    { when: 'T-5 days',  milestone: 'File Import Notification & endorsements',when_date: '' },
    { when: 'Day 0',     milestone: 'Departure',                              when_date: '' },
  ];

  const isEffectivelyEmpty = (list, keys) =>
    !list || list.length === 0 || list.every((it) => keys.every((k) => !((it?.[k] || '').toString().trim())));

  const applyTemplate = (tpl, { source = 'manual' } = {}) => {
    const nsEmpty = isEffectivelyEmpty(form.next_steps, ['action', 'owner', 'due_date']);
    const tlEmpty = isEffectivelyEmpty(form.timeline_items, ['when', 'milestone', 'when_date']);
    if (!nsEmpty || !tlEmpty) {
      if (!window.confirm(`Replace current Next Steps and Timeline with the "${tpl.name}" template?`)) return;
    }
    const nextSteps = tpl.next_steps?.length ? tpl.next_steps : FALLBACK_NEXT_STEPS;
    const timeline = tpl.timeline_items?.length ? tpl.timeline_items : FALLBACK_TIMELINE;
    // Snapshot the prior state so we can undo cleanly.
    const prior = {
      next_steps: JSON.parse(JSON.stringify(form.next_steps || [])),
      timeline_items: JSON.parse(JSON.stringify(form.timeline_items || [])),
    };
    setForm((p) => ({
      ...p,
      next_steps: nextSteps.map((s) => ({ ...s })),
      timeline_items: timeline.map((t) => ({ ...t })),
    }));
    setAppliedTemplate({ id: tpl.id || null, name: tpl.name, source, prior });
    toast.success(`Prefilled from "${tpl.name}"`);
    setTplPickerOpen(false);
  };

  const undoAppliedTemplate = () => {
    if (!appliedTemplate) return;
    setForm((p) => ({
      ...p,
      next_steps: appliedTemplate.prior.next_steps,
      timeline_items: appliedTemplate.prior.timeline_items,
    }));
    setAppliedTemplate(null);
    toast.success('Reverted template');
  };

  const onPrefillClick = () => {
    if (templates.length === 0) {
      // Nothing to pick from — apply the built-in fallback so the button still works.
      applyTemplate({ name: 'Standard fallback', next_steps: FALLBACK_NEXT_STEPS, timeline_items: FALLBACK_TIMELINE }, { source: 'manual' });
      return;
    }
    if (templates.length === 1) {
      applyTemplate(templates[0], { source: 'manual' });
      return;
    }
    setTplPickerOpen(true);
  };

  // ---------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------
  const save = useCallback(async () => {
    setSaving(true);
    try {
      // Strip empty rows
      const payload = {
        ...form,
        discussion_points: form.discussion_points.filter((p) => (p || '').trim()),
        next_steps:        form.next_steps.filter((s) => (s.action || '').trim()),
        timeline_items:    form.timeline_items.filter((t) => (t.when || t.milestone || '').trim()),
        transit_stops:     (form.transit_stops || []).filter((s) => (s.location || '').trim()),
      };
      let sid = savedId;
      let saved;
      if (!sid) {
        const { data } = await axios.post(`${API}/admin/consultation-summaries`, payload, { headers: auth });
        saved = data;
        sid = data.id;
        setSavedId(sid);
        // Rewrite URL so subsequent saves are PUTs and the PDF preview works.
        window.history.replaceState({}, '', `/admin/consultation-summaries/${sid}/edit`);
      } else {
        const { data } = await axios.put(`${API}/admin/consultation-summaries/${sid}`, payload, { headers: auth });
        saved = data;
      }
      setForm((old) => ({ ...old, client_id: saved.client_id || null, client_sync: saved.client_sync || null }));
      setPreviewKey((k) => k + 1);
      toast.success('Saved');
      return sid;
    } catch (e) {
      logger.error('save summary failed', e);
      toast.error('Save failed');
      return null;
    } finally {
      setSaving(false);
    }
  }, [form, savedId, auth]);

  // ---------------------------------------------------------------
  // Send
  // ---------------------------------------------------------------
  const openSendModal = async () => {
    if (preparingEmailRef.current || saving) return;
    preparingEmailRef.current = true;
    setPreparingEmail(true);
    try {
      const sid = await save();
      if (!sid) return; // Never prepare/send an old PDF after a failed save.
      const { data } = await axios.post(`${API}/admin/consultation-summaries/${sid}/email-preview`, {}, { headers: auth });
      setEmailDraft(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not prepare the email preview');
    } finally {
      setPreparingEmail(false);
      preparingEmailRef.current = false;
    }
  };

  const openComposer = async () => {
    const sid = await save();
    if (!sid) return;
    const params = new URLSearchParams();
    if (form.client_email) params.set('to', form.client_email);
    params.set('subject', `Consultation summary — ${form.title || form.client_name || ''}`.trim());
    params.set('attach', `consultation_summary:${sid}`);
    navigate(`/admin/email-composer?${params.toString()}`);
  };

  // ---------------------------------------------------------------
  // Snippets
  // ---------------------------------------------------------------
  const openSnippetPicker = (kind, onPick) => setSnippetPicker({ kind, onPick });
  const closeSnippetPicker = () => setSnippetPicker(null);

  const saveSnippet = async (kind, sourceText) => {
    const text = (sourceText || '').trim();
    if (!text) { toast.error('Nothing to save'); return; }
    const label = window.prompt('Give this snippet a short label:', text.slice(0, 40));
    if (!label) return;
    try {
      const { data } = await axios.post(
        `${API}/admin/consultation-snippets`,
        { kind, label, body: text },
        { headers: auth },
      );
      setSnippets((prev) => [...prev, data]);
      toast.success('Snippet saved');
    } catch (e) { toast.error('Save snippet failed'); }
  };

  const removeSnippet = async (sid) => {
    if (!window.confirm('Delete this snippet?')) return;
    try {
      await axios.delete(`${API}/admin/consultation-snippets/${sid}`, { headers: auth });
      setSnippets((prev) => prev.filter((x) => x.id !== sid));
    } catch (e) { toast.error('Delete snippet failed'); }
  };

  const openPdfInNewTab = async () => {
    if (!savedId) return;
    try {
      const res = await fetch(`${API}/admin/consultation-summaries/${savedId}/pdf`, { headers: auth });
      if (!res.ok) throw new Error('load failed');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) { toast.error('Could not open PDF'); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center text-foreground/60">
      <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="consultation-summary-editor">
      {/* Header bar */}
      <div className="bg-white border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex min-w-0 items-center gap-3">
            <button onClick={() => navigate('/admin/consultation-summaries')} className="p-2 rounded-lg hover:bg-gray-100" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></button>
            <div className="w-10 h-10 shrink-0 bg-primary/10 rounded-lg flex items-center justify-center"><ClipboardCheck className="w-5 h-5 text-primary" /></div>
            <div className="min-w-0 break-words">
              <h1 className="text-base sm:text-xl font-bold text-primary">{isNew ? 'New consultation summary' : 'Edit summary'}</h1>
              <p className="text-xs text-foreground/60">{form.client_name || 'Untitled'}{form.destination ? ` · → ${form.destination}` : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {savedId && (
              <button onClick={openPdfInNewTab} className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50" data-testid="open-pdf">
                <ExternalLink className="w-4 h-4" /> Open PDF
              </button>
            )}
            <Button variant="outline" onClick={save} disabled={saving} data-testid="save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
            </Button>
            <Button variant="outline" onClick={openComposer} disabled={saving || preparingEmail} data-testid="open-composer-btn">
              <Copy className="w-4 h-4 mr-1.5" /> Open in composer
            </Button>
            <Button onClick={openSendModal} disabled={saving || preparingEmail} data-testid="send-btn">
              {preparingEmail ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} {preparingEmail ? 'Preparing preview…' : 'Email to client'}
            </Button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="container mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ------------ Left form ------------ */}
        <div className="min-w-0 lg:col-span-3 space-y-5">
          <ApprovedConsultationPdf summaryId={savedId} auth={auth} refreshKey={previewKey} disabled={saving || preparingEmail} save={save} onState={setApprovedPdfState} onChange={(data) => { setForm((old) => ({ ...old, approved_pdf: data.approved_pdf, approved_pdf_version: data.version })); setPreviewKey((key) => key + 1); }} />
          {/* Applied-template banner */}
          {appliedTemplate && (
            <div
              className="flex flex-wrap items-center justify-between gap-3 bg-teal-50 border border-teal-200 rounded-xl px-4 py-3"
              data-testid="applied-template-banner"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-teal-700" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-teal-900 truncate">
                    Applied template: {appliedTemplate.name}
                  </div>
                  <div className="text-[11px] text-teal-700/80">
                    {appliedTemplate.source === 'auto'
                      ? 'Auto-matched from the linked quote\u2019s destination.'
                      : 'Manually prefilled.'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="outline" size="sm" onClick={undoAppliedTemplate} data-testid="applied-template-undo">
                  <RotateCcw className="w-4 h-4 mr-1" /> Undo
                </Button>
                <Button variant="outline" size="sm" onClick={() => setTplPickerOpen(true)} data-testid="applied-template-switch">
                  <Repeat className="w-4 h-4 mr-1" /> Switch…
                </Button>
                <button
                  onClick={() => setAppliedTemplate(null)}
                  title="Dismiss (keep the applied template)"
                  className="p-2 rounded hover:bg-teal-100 text-teal-700"
                  data-testid="applied-template-dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Meta */}
          <div className="bg-white border border-border rounded-xl p-5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-primary mb-3">Meta</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Title (shown at top of PDF)</Label>
                <Input value={form.title || ''} onChange={(e) => setField('title', e.target.value)} placeholder="e.g. Kickoff call — Rocky's UK relocation" data-testid="field-title" />
              </div>
              <div>
                <Label>Consultation date</Label>
                <Input type="date" value={form.consultation_date || ''} onChange={(e) => setField('consultation_date', e.target.value)} data-testid="field-date" />
              </div>
              <div>
                <Label>Attendees</Label>
                <Input value={form.attendees || ''} onChange={(e) => setField('attendees', e.target.value)} placeholder="Dr. Samantha Dorman, Jane Smith" data-testid="field-attendees" />
              </div>
              <div className="sm:col-span-2">
                <Label>Link to a quote (optional)</Label>
                <select
                  value={form.linked_quote_id || ''}
                  onChange={(e) => onSelectQuote(e.target.value)}
                  data-testid="field-linked-quote"
                  className="w-full min-w-0 max-w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                >
                  <option value="">— Standalone (no linked quote) —</option>
                  {quotes.map((q) => (
                    <option key={q.id} value={q.id}>{`${q.quote_number || q.id.slice(0, 8)} · ${q.client_name || 'Unknown'}${q.destination ? ` → ${q.destination}` : ''}`}</option>
                  ))}
                </select>
                {form.linked_quote_number && (
                  <p className="text-xs text-foreground/60 mt-1">Linked to quote <strong>{form.linked_quote_number}</strong></p>
                )}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <Button type="button" variant="outline" size="sm" className="h-auto min-h-9 max-w-full whitespace-normal text-left" onClick={onPrefillClick} data-testid="prefill-template-btn">
                    <Sparkles className="w-4 h-4 mr-1" /> {templates.length > 1 ? 'Prefill from template…' : 'Prefill common steps & timeline'}
                  </Button>
                  <span className="text-[11px] text-foreground/50">
                    {templates.length > 1
                      ? `${templates.length} named templates available (UK, US, EU…). Manage them in Settings.`
                      : 'Seeds the standard pet-travel actions and T-week milestones with sensible owners.'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Client */}
          <div className="bg-white border border-border rounded-xl p-5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-primary mb-3">Client details</h2>
            <ConsultationClientLink form={form} auth={auth} onChoose={(client) => setForm((old) => ({ ...old,
              client_id: client.id, client_name: client.name || '', client_email: client.email || '', client_phone: client.phone || '', client_address: client.address || '',
              client_sync: { status: 'selected', message: 'Client selected. Save the summary to confirm the link.' },
            }))} onUnlink={() => setForm((old) => ({ ...old, client_id: null, client_sync: null }))} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Client name</Label>
                <Input value={form.client_name || ''} onChange={(e) => setField('client_name', e.target.value)} data-testid="field-client-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.client_email || ''} onChange={(e) => setForm((old) => ({ ...old, client_email: e.target.value, client_id: null, client_sync: null }))} data-testid="field-client-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.client_phone || ''} onChange={(e) => setField('client_phone', e.target.value)} data-testid="field-client-phone" />
              </div>
              <div>
                <Label>Address</Label>
                <Input value={form.client_address || ''} onChange={(e) => setField('client_address', e.target.value)} data-testid="field-client-address" />
              </div>
              <div>
                <Label>Pet name(s)</Label>
                <Input value={form.pet_names || ''} onChange={(e) => setField('pet_names', e.target.value)} placeholder="Rocky, Luna" data-testid="field-pets" />
              </div>
              <div>
                <Label>Origin</Label>
                <Input value={form.origin || ''} onChange={(e) => setField('origin', e.target.value)} placeholder="Grand Cayman" data-testid="field-origin" />
              </div>
              <div>
                <Label>Destination</Label>
                <Input value={form.destination || ''} onChange={(e) => setField('destination', e.target.value)} placeholder="United Kingdom" data-testid="field-destination" />
              </div>
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between">
                  <Label>In transit via</Label>
                  <button
                    type="button"
                    onClick={() => setField('transit_via', 'N/A')}
                    className="text-[11px] text-primary hover:underline"
                    data-testid="transit-via-na"
                  >Set N/A</button>
                </div>
                <Input value={form.transit_via || ''} onChange={(e) => setField('transit_via', e.target.value)} placeholder='e.g. "Miami (MIA)" or "N/A"' data-testid="field-transit-via" />
                <p className="text-[11px] text-foreground/50 mt-1">Used when the route has no stops. Add stops below for a multi-leg route strip on the PDF.</p>
              </div>
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <Label>Multi-leg transit stops</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => addListRow('transit_stops', { location: '', dwell: '' })} data-testid="ts-add">
                    <Plus className="w-4 h-4 mr-1" /> Add stop
                  </Button>
                </div>
                {(form.transit_stops || []).length === 0 ? (
                  <p className="text-[11px] text-foreground/50">No stops — direct route. PDF will show the "In transit via" tile above.</p>
                ) : (
                  <div className="space-y-2">
                    {form.transit_stops.map((s, i) => (
                      <div key={i} className="border border-border rounded-lg p-2.5 space-y-2 bg-gray-50/50" data-testid={`ts-row-${i}`}>
                        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_130px_auto] gap-2 items-center">
                          <Input
                            value={s.location || ''}
                            onChange={(e) => setListValue('transit_stops', i, 'location', e.target.value)}
                            placeholder="Location (e.g. Miami (MIA))"
                            data-testid={`ts-location-${i}`}
                          />
                          <Input
                            value={s.dwell || ''}
                            onChange={(e) => setListValue('transit_stops', i, 'dwell', e.target.value)}
                            placeholder="Dwell (e.g. 6h)"
                            data-testid={`ts-dwell-${i}`}
                          />
                          <button
                            onClick={() => removeListRow('transit_stops', i)}
                            className="p-2 rounded hover:bg-red-50 text-red-600"
                            data-testid={`ts-remove-${i}`}
                          ><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={s.carrier || ''}
                            onChange={(e) => setListValue('transit_stops', i, 'carrier', e.target.value)}
                            placeholder="Carrier (e.g. BA)"
                            data-testid={`ts-carrier-${i}`}
                          />
                          <Input
                            value={s.flight_number || ''}
                            onChange={(e) => setListValue('transit_stops', i, 'flight_number', e.target.value)}
                            placeholder="Flight # (e.g. 251)"
                            data-testid={`ts-flight-${i}`}
                          />
                        </div>
                      </div>
                    ))}
                    <p className="text-[11px] text-foreground/50 mt-1">Renders as: <span className="font-medium">{form.origin || 'Origin'} → {form.transit_stops.filter((s) => s.location).map((s) => {
                      const flight = [s.carrier, s.flight_number].filter(Boolean).join(' ').trim();
                      const bits = [flight, s.dwell].filter(Boolean).join(' · ');
                      return `${s.location}${bits ? ` (${bits})` : ''}`;
                    }).join(' → ')} → {form.destination || 'Destination'}</span></p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Intro */}
          <div className="bg-white border border-border rounded-xl p-5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-primary mb-3">Summary of the conversation</h2>
            <textarea
              value={form.intro || ''}
              onChange={(e) => setField('intro', e.target.value)}
              rows={4}
              placeholder="One-paragraph recap of the call — what you covered, tone, any decisions made."
              data-testid="field-intro"
              className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Discussion points */}
          <div className="bg-white border border-border rounded-xl p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 className="text-sm font-bold uppercase tracking-wide text-primary">What we discussed</h2>
              <div className="flex items-center gap-2">
                <button onClick={() => openSnippetPicker('discussion', (body) => addListRow('discussion_points', body) || setForm((p) => { const arr = [...p.discussion_points]; arr[arr.length - 1] = body; return { ...p, discussion_points: arr }; }))} className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-border rounded-md hover:bg-gray-50" data-testid="dp-snippets">
                  <Sparkles className="w-3.5 h-3.5" /> Snippets
                </button>
                <Button variant="outline" size="sm" onClick={() => addListRow('discussion_points', '')} data-testid="dp-add"><Plus className="w-4 h-4 mr-1" /> Add point</Button>
              </div>
            </div>
            <div className="space-y-2">
              {form.discussion_points.map((v, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="w-5 text-xs font-mono text-foreground/40 pt-2">{i + 1}.</span>
                  <textarea
                    value={v}
                    onChange={(e) => setListValue('discussion_points', i, null, e.target.value)}
                    rows={2}
                    data-testid={`dp-${i}`}
                    className="min-w-0 flex-1 px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <button onClick={() => saveSnippet('discussion', v)} className="p-1.5 rounded hover:bg-primary/10 text-primary" title="Save as snippet" data-testid={`dp-save-snippet-${i}`}><Sparkles className="w-4 h-4" /></button>
                  <button onClick={() => removeListRow('discussion_points', i)} className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`dp-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Next steps */}
          <div className="bg-white border border-border rounded-xl p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 className="text-sm font-bold uppercase tracking-wide text-primary">Agreed next steps</h2>
              <div className="flex items-center gap-2">
                <button onClick={() => openSnippetPicker('next_step', (body) => { addListRow('next_steps', { action: '', owner: '', due_date: '' }); setForm((p) => { const arr = [...p.next_steps]; arr[arr.length - 1] = { ...arr[arr.length - 1], action: body }; return { ...p, next_steps: arr }; }); })} className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-border rounded-md hover:bg-gray-50" data-testid="ns-snippets">
                  <Sparkles className="w-3.5 h-3.5" /> Snippets
                </button>
                <Button variant="outline" size="sm" onClick={() => addListRow('next_steps', { action: '', owner: '', due_date: '' })} data-testid="ns-add"><Plus className="w-4 h-4 mr-1" /> Add step</Button>
              </div>
            </div>
            <div className="space-y-3">
              {form.next_steps.map((s, i) => (
                <div key={i} className="border border-border rounded-lg p-3 space-y-2 bg-gray-50/50" data-testid={`ns-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-foreground/60 uppercase">Step {i + 1}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => saveSnippet('next_step', s.action)} className="p-1 rounded hover:bg-primary/10 text-primary" title="Save action as snippet" data-testid={`ns-save-snippet-${i}`}><Sparkles className="w-4 h-4" /></button>
                      <button onClick={() => removeListRow('next_steps', i)} className="p-1 rounded hover:bg-red-50 text-red-600" data-testid={`ns-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <textarea
                    value={s.action}
                    onChange={(e) => setListValue('next_steps', i, 'action', e.target.value)}
                    rows={2}
                    placeholder="Action…"
                    data-testid={`ns-action-${i}`}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input
                      value={s.owner || ''}
                      onChange={(e) => setListValue('next_steps', i, 'owner', e.target.value)}
                      placeholder="Owner (e.g. Jane, Pawsome)"
                      data-testid={`ns-owner-${i}`}
                    />
                    {String(s.due_date || '').toUpperCase() === 'TBD' ? (
                      <div className="flex items-center gap-1">
                        <div className="flex-1 px-3 py-2 text-sm border border-border rounded-md bg-teal-50 text-teal-800 font-semibold text-center" data-testid={`ns-due-tbd-${i}`}>
                          To be determined
                        </div>
                        <button
                          type="button"
                          onClick={() => setListValue('next_steps', i, 'due_date', '')}
                          className="p-2 rounded hover:bg-gray-100 text-foreground/60 text-xs"
                          title="Clear TBD and pick a date"
                          data-testid={`ns-due-clear-tbd-${i}`}
                        ><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Input
                          type="date"
                          value={s.due_date || ''}
                          onChange={(e) => setListValue('next_steps', i, 'due_date', e.target.value)}
                          data-testid={`ns-due-${i}`}
                          className="min-w-0 flex-1"
                        />
                        <button
                          type="button"
                          onClick={() => setListValue('next_steps', i, 'due_date', 'TBD')}
                          className="whitespace-nowrap px-2 py-2 text-[11px] font-semibold border border-border rounded-md hover:bg-teal-50 hover:text-teal-700 text-foreground/70"
                          title="Mark due date as To be determined"
                          data-testid={`ns-due-tbd-btn-${i}`}
                        >TBD</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white border border-border rounded-xl p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 className="text-sm font-bold uppercase tracking-wide text-primary">Timeline & milestones</h2>
              <Button variant="outline" size="sm" onClick={() => addListRow('timeline_items', { when: '', milestone: '' })} data-testid="tl-add"><Plus className="w-4 h-4 mr-1" /> Add milestone</Button>
            </div>
            <div className="space-y-2">
              {form.timeline_items.map((t, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[110px_130px_minmax(0,1fr)_auto] gap-2 items-start" data-testid={`tl-row-${i}`}>
                  <Input
                    value={t.when || ''}
                    onChange={(e) => setListValue('timeline_items', i, 'when', e.target.value)}
                    placeholder="T-6 weeks"
                    data-testid={`tl-when-${i}`}
                  />
                  <Input
                    type="date"
                    value={t.when_date || ''}
                    onChange={(e) => setListValue('timeline_items', i, 'when_date', e.target.value)}
                    title="Optional exact date — used to highlight the closest-upcoming milestone as NEXT UP"
                    data-testid={`tl-when-date-${i}`}
                  />
                  <Input
                    value={t.milestone || ''}
                    onChange={(e) => setListValue('timeline_items', i, 'milestone', e.target.value)}
                    placeholder="Milestone"
                    data-testid={`tl-milestone-${i}`}
                  />
                  <button onClick={() => removeListRow('timeline_items', i)} className="p-2 rounded hover:bg-red-50 text-red-600" data-testid={`tl-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <p className="text-[11px] text-foreground/50">Add an optional exact date to highlight the closest-upcoming milestone with a "NEXT UP" tag on the PDF.</p>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white border border-border rounded-xl p-5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-primary mb-3">Notes & special considerations</h2>
            <textarea
              value={form.notes || ''}
              onChange={(e) => setField('notes', e.target.value)}
              rows={3}
              placeholder="Anything to flag — health quirks, travel-day preferences, family arrival dates, etc."
              data-testid="field-notes"
              className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {/* ------------ Right rail: Live HTML preview ------------ */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-border rounded-xl p-3 sticky top-24">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-foreground/60 uppercase" data-testid="summary-preview-kind">{approvedPdfState?.approved_pdf ? 'Approved PDF' : 'Live text preview'}</span>
              <span className="text-[11px] text-foreground/40" data-testid="summary-preview-status">{approvedPdfState?.approved_pdf ? 'Saved attachment' : 'Updates as you type'}</span>
            </div>
            <div className="bg-gray-100 rounded-lg border border-border max-h-[calc(100vh-180px)] overflow-y-auto" data-testid="pdf-preview-frame">
              {approvedPdfState?.approved_pdf ? <ConsultationPdfViewer key={approvedPdfState.approved_pdf.id} url={`${API}/admin/consultation-summaries/${savedId}/pdf-preview`} auth={auth} label={approvedPdfState.approved_pdf.filename} prefix="approved-summary-preview" /> : <ConsultationSummaryPreview form={form} />}
            </div>
            {savedId && (
              <button
                onClick={openPdfInNewTab}
                className="w-full mt-2 text-xs text-primary hover:underline flex items-center justify-center gap-1"
                data-testid="preview-open-pdf"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open full PDF in a new tab
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Template picker modal — appears when there are multiple named templates */}
      {tplPickerOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 flex items-center justify-center p-4" onClick={() => setTplPickerOpen(false)}>
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="template-picker">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="text-sm font-semibold">Prefill from which template?</h3>
              <button onClick={() => setTplPickerOpen(false)} className="p-1 rounded hover:bg-gray-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-3 overflow-y-auto flex-1 space-y-2">
              {templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => applyTemplate(t)}
                  className="w-full text-left border border-border rounded-lg p-3 hover:border-primary hover:bg-primary/5 transition-colors"
                  data-testid={`template-pick-${t.id}`}
                >
                  <div className="text-sm font-semibold text-primary">{t.name}</div>
                  <div className="text-[11px] text-foreground/60 mt-0.5">
                    {(t.next_steps?.length || 0)} next-step{t.next_steps?.length === 1 ? '' : 's'} · {(t.timeline_items?.length || 0)} milestone{t.timeline_items?.length === 1 ? '' : 's'}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Snippet picker modal */}
      {snippetPicker && (
        <div className="fixed inset-0 bg-black/50 z-30 flex items-center justify-center p-4" onClick={closeSnippetPicker}>
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="snippet-picker">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="text-sm font-semibold">Insert {snippetPicker.kind === 'discussion' ? 'discussion point' : 'next-step'} snippet</h3>
              <button onClick={closeSnippetPicker} className="p-1 rounded hover:bg-gray-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-3 overflow-y-auto flex-1 space-y-2">
              {snippets.filter((s) => s.kind === snippetPicker.kind).length === 0 ? (
                <p className="text-xs text-foreground/60 p-4 text-center">No snippets saved yet. Save one from a row using the sparkle icon.</p>
              ) : snippets.filter((s) => s.kind === snippetPicker.kind).map((s) => (
                <div key={s.id} className="border border-border rounded-lg p-3 hover:border-primary/40" data-testid={`snippet-${s.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-semibold">{s.label}</h4>
                    <button onClick={() => removeSnippet(s.id)} className="p-1 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <p className="text-xs text-foreground/70 line-clamp-2 mb-2">{s.body}</p>
                  <Button size="sm" variant="outline" onClick={() => { snippetPicker.onPick(s.body); closeSnippetPicker(); }} data-testid={`snippet-insert-${s.id}`}>
                    Insert
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Send modal */}
      {emailDraft && <ConsultationEmailReviewDialog initial={emailDraft} auth={auth} onClose={() => setEmailDraft(null)} onSent={(result) => {
        setEmailDraft(null);
        setForm((old) => ({ ...old, status: 'sent', last_sent_to: result.sent_to }));
        toast.success(`Sent to ${result.sent_to}`);
      }} />}
    </div>
  );
};

export default ConsultationSummaryEditor;
